/**
 * Working hours calendar: month grid, daily entries.
 * Fetches from GET /api/working-hours?month=&year=; save via POST (upsert).
 */
import React, { useState, useEffect, useMemo } from 'react';
import hrApi from '../api';
import DayEntryModal from './DayEntryModal';
import { formatDecimalHours, formatHHmmToAmPm } from '../utils/formatTime';
import './WorkingHoursCalendar.css';

const HALF_DAY_WORK_SLOT = {
  am: { startTime: '13:00', endTime: '17:12' }, // AM off => work in afternoon
  pm: { startTime: '08:00', endTime: '12:00' }, // PM off => work in morning
};

const WORKING_HOURS_SUBMISSION_LOCK_DAYS = 30;

/** UTC calendar YYYY-MM-DD (matches backend stored `date`). */
function utcYmdFromDateLike(d) {
  if (d == null) return null;
  if (typeof d === 'string' && d.length >= 10) return d.slice(0, 10);
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  return t.toISOString().slice(0, 10);
}

function calendarDaysBetweenUtcYmd(entryYmd, todayYmd) {
  if (!entryYmd || !todayYmd) return 0;
  const [ey, em, ed] = entryYmd.split('-').map(Number);
  const [ty, tm, td] = todayYmd.split('-').map(Number);
  if ([ey, em, ed, ty, tm, td].some((n) => Number.isNaN(n))) return 0;
  const entryMs = Date.UTC(ey, em - 1, ed);
  const todayMs = Date.UTC(ty, tm - 1, td);
  return Math.floor((todayMs - entryMs) / 86400000);
}

function isWorkingHourYmdLockedForEmployee(entryYmd) {
  const todayYmd = new Date().toISOString().slice(0, 10);
  return calendarDaysBetweenUtcYmd(entryYmd, todayYmd) > WORKING_HOURS_SUBMISSION_LOCK_DAYS;
}

/** Manager/admin may only edit after the employee window; recent dates are view-only. */
function isWorkingHourYmdLockedForManagerCorrection(entryYmd) {
  if (!entryYmd) return false;
  return !isWorkingHourYmdLockedForEmployee(entryYmd);
}

function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseHHmmToHours(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split(':').map(Number);
  if (parts.length < 2) return null;
  const h = parts[0] + (parts[1] || 0) / 60;
  return Number.isNaN(h) ? null : h;
}

/** Compute break duration in hours from entry (start, end, totalHours). Returns null if no break or invalid. */
function getBreakHours(entry) {
  if (!entry || entry.entryMode !== 'time' || !entry.startTime || !entry.endTime || entry.totalHours == null) return null;
  const startH = parseHHmmToHours(entry.startTime);
  const endH = parseHHmmToHours(entry.endTime);
  if (startH == null || endH == null || endH <= startH) return null;
  const span = endH - startH;
  const total = Number(entry.totalHours) || 0;
  const breakH = Math.round((span - total) * 100) / 100;
  return breakH > 0 ? breakH : null;
}

// Monday = 0, Tuesday = 1, …, Sunday = 6 (calendar starts on Monday)
function getDaysInMonth(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days = [];
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d));
  }
  const startWeekday = (first.getDay() + 6) % 7;
  return { days, startWeekday };
}

function WorkingHoursCalendar({
  adminId = null,
  readOnly = false,
  applyEmployeeSubmissionLock = false,
  applyManagerCorrectionLock = false,
  defaultsRefreshKey = 0,
  approvedLeavesRefreshKey = 0,
  intro = null,
}) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalDay, setModalDay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);

  const entriesByDate = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      const key =
        e.date && typeof e.date === 'string' && e.date.length >= 10
          ? e.date.slice(0, 10)
          : toDateKey(new Date(e.date));
      map[key] = e;
    });
    return map;
  }, [entries]);

  const fetchMonth = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { month, year };
      if (adminId) params.adminId = adminId;
      const res = await hrApi.get('/api/working-hours', { params });
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load working hours');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonth();
  }, [month, year, adminId]);

  useEffect(() => {
    if (readOnly && !adminId) return;
    const params = adminId ? { adminId } : {};
    hrApi.get('/api/default-working-hours', { params }).then((res) => setDefaults(res.data)).catch(() => setDefaults(null));
  }, [readOnly, adminId, defaultsRefreshKey]);

  const firstDayOfMonth = useMemo(() => `${year}-${String(month).padStart(2, '0')}-01`, [year, month]);
  const lastDayOfMonth = useMemo(() => {
    const d = new Date(year, month, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [year, month]);

  useEffect(() => {
    const params = { status: 'approved', startDate: firstDayOfMonth, endDate: lastDayOfMonth };
    if (adminId) params.adminId = adminId;
    hrApi.get('/api/holidays', { params })
      .then((res) => setApprovedLeaves(Array.isArray(res.data) ? res.data : []))
      .catch(() => setApprovedLeaves([]));
  }, [adminId, firstDayOfMonth, lastDayOfMonth, approvedLeavesRefreshKey]);

  useEffect(() => {
    hrApi.get('/api/public-holidays', { params: { year } })
      .then((res) => setPublicHolidays(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPublicHolidays([]));
  }, [year]);

  const publicHolidayByDate = useMemo(() => {
    const map = {};
    publicHolidays.forEach((ph) => {
      if (!ph || !ph.date) return;
      // Always use the UTC date portion (ISO slice) so that the key matches
      // the stored calendar day regardless of the browser's local timezone.
      // setHours(0,0,0,0) would shift to local midnight and produce the wrong
      // day key in any timezone that is behind UTC.
      const startKey = typeof ph.date === 'string' && ph.date.length >= 10
        ? ph.date.slice(0, 10)
        : new Date(ph.date).toISOString().slice(0, 10);
      const endKey = ph.endDate
        ? (typeof ph.endDate === 'string' && ph.endDate.length >= 10
          ? ph.endDate.slice(0, 10)
          : new Date(ph.endDate).toISOString().slice(0, 10))
        : startKey;
      const label = ph.name || ph.nameFr || 'Public holiday';
      let d = new Date(startKey + 'T00:00:00.000Z');
      const endDay = new Date(endKey + 'T00:00:00.000Z');
      while (d <= endDay) {
        map[d.toISOString().slice(0, 10)] = label;
        d = new Date(d.getTime() + 86400000);
      }
    });
    return map;
  }, [publicHolidays]);

  /** True if dateKey is a working day for this employee (not weekend, not off in schedule). Leave is only shown on working days. */
  const isWorkingDayDateKey = useMemo(() => {
    const wd = Array.isArray(defaults?.workingDays) && defaults.workingDays.length > 0 ? defaults.workingDays : [1, 2, 3, 4, 5];
    const schedule = defaults?.daySchedule && typeof defaults.daySchedule === 'object' ? defaults.daySchedule : {};
    return (dateKey) => {
      const d = new Date(dateKey + 'T12:00:00');
      const dayOfWeek = d.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) return false;
      if (schedule[String(dayOfWeek)] === 'off') return false;
      return wd.includes(dayOfWeek);
    };
  }, [defaults?.workingDays, defaults?.daySchedule]);

  const { leaveDateKeys, halfDayLeaveByDate } = useMemo(() => {
    const set = new Set();
    const halfByDate = {};
    const isActuallyWorkingDay = (key) => isWorkingDayDateKey(key) && !publicHolidayByDate[key];
    approvedLeaves.forEach((h) => {
      const start = new Date(h.startDate);
      const end = new Date(h.endDate);
      const startKey = toDateKey(start);
      const endKey = toDateKey(end);
      const isSingleDay = startKey === endKey;
      const halfDay = h.halfDay === 'am' || h.halfDay === 'pm' ? h.halfDay : null;

      if (isSingleDay && halfDay) {
        if (isActuallyWorkingDay(startKey)) {
          set.add(startKey);
          halfByDate[startKey] = halfDay;
        }
        return;
      }
      const d = new Date(start);
      d.setHours(0, 0, 0, 0);
      const endDate = new Date(end);
      endDate.setHours(0, 0, 0, 0);
      while (d <= endDate) {
        const key = toDateKey(d);
        if (isActuallyWorkingDay(key)) set.add(key);
        d.setDate(d.getDate() + 1);
      }
    });
    return { leaveDateKeys: set, halfDayLeaveByDate: halfByDate };
  }, [approvedLeaves, isWorkingDayDateKey, publicHolidayByDate]);

  const workingDays = useMemo(() => {
    const wd = defaults?.workingDays;
    return Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5];
  }, [defaults?.workingDays]);

  const { days, startWeekday } = useMemo(
    () => getDaysInMonth(year, month - 1),
    [year, month]
  );

  const monthName = useMemo(() => {
    return new Date(year, month - 1, 1).toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
  }, [year, month]);

  const handlePrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const handleSaveDay = async (payload) => {
    setSaving(true);
    setError('');
    try {
      await hrApi.post('/api/working-hours', {
        ...payload,
        totalHours: Number(payload.totalHours),
        ...(adminId ? { adminId } : {}),
      });
      setModalDay(null);
      fetchMonth();
    } catch (err) {
      const msg = err?.response?.data?.message;
      const status = err?.response?.status;
      const detail = msg || (status === 401 ? 'Please log in again.' : status === 403 ? 'You do not have permission.' : 'Failed to save.');
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDay = async (id) => {
    setSaving(true);
    setError('');
    try {
      await hrApi.delete(`/api/working-hours/${id}`);
      setModalDay(null);
      fetchMonth();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to remove entry');
    } finally {
      setSaving(false);
    }
  };

  const toolbarHasIntro = !!(intro || applyEmployeeSubmissionLock || applyManagerCorrectionLock);

  const modalLockedBySubmission = useMemo(() => {
    if (!modalDay || !applyEmployeeSubmissionLock) return false;
    const y = modalDay.entry ? utcYmdFromDateLike(modalDay.entry.date) : modalDay.date;
    return !!(y && isWorkingHourYmdLockedForEmployee(y));
  }, [modalDay, applyEmployeeSubmissionLock]);

  const modalLockedByManagerCorrection = useMemo(() => {
    if (!modalDay || !applyManagerCorrectionLock) return false;
    const y = modalDay.entry ? utcYmdFromDateLike(modalDay.entry.date) : modalDay.date;
    return !!(y && isWorkingHourYmdLockedForManagerCorrection(y));
  }, [modalDay, applyManagerCorrectionLock]);

  return (
    <div className="working-hours-calendar">
      <div
        className={`wh-calendar-toolbar d-flex flex-wrap gap-2 gap-md-3 mb-3 align-items-center ${
          toolbarHasIntro ? 'justify-content-between' : 'justify-content-center'
        }`}
      >
        {toolbarHasIntro ? (
          <div className="wh-calendar-toolbar-intro flex-grow-1 me-md-2" style={{ minWidth: 0 }}>
            {intro}
            {applyEmployeeSubmissionLock ? (
              <p className={`text-muted small mb-0${intro ? ' mt-1' : ''}`}>
                Entries older than 30 days are read-only; contact HR or your manager to correct.
              </p>
            ) : null}
            {applyManagerCorrectionLock ? (
              <p className={`text-muted small mb-0${intro || applyEmployeeSubmissionLock ? ' mt-1' : ''}`}>
                You can only edit dates more than 30 days ago; recent dates are for the employee to update.
              </p>
            ) : null}
          </div>
        ) : null}
        <div className={`d-flex align-items-center gap-2 flex-shrink-0${toolbarHasIntro ? ' ms-auto' : ''}`}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handlePrevMonth}
            aria-label="Previous month"
          >
            <i className="bi bi-chevron-left" />
          </button>
          <span className="wh-month-title">{monthName}</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="wh-loading">Loading...</div>
      ) : (
        <div className="wh-grid">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((wd) => (
            <div key={wd} className="wh-cell wh-header">
              {wd}
            </div>
          ))}
          {Array.from({ length: startWeekday }, (_, i) => (
            <div key={`empty-${i}`} className="wh-cell wh-empty" />
          ))}
          {days.map((day) => {
            const key = toDateKey(day);
            const entry = entriesByDate[key];
            const todayKey = toDateKey(now);
            const isToday = key === todayKey;
            const isPastOrToday = key <= todayKey;
            const dayOfWeek = day.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            const isWorkingDay = !isWeekend && workingDays.includes(dayOfWeek);
            const isNonWorkingWeekday = !isWeekend && !isWorkingDay;
            // If there are entered hours for this day, show them (don’t show “Off day” / “Weekend”).
            // Off-day applies only when there’s no data (future off-days and past off-days with no entry).
            const hasEntry = !!entry;
            const lockYmd = hasEntry ? utcYmdFromDateLike(entry.date) : key;
            const submissionLocked =
              applyEmployeeSubmissionLock && lockYmd && isWorkingHourYmdLockedForEmployee(lockYmd);
            const managerCorrectionLocked =
              applyManagerCorrectionLock && lockYmd && isWorkingHourYmdLockedForManagerCorrection(lockYmd);
            const dayEditable = !readOnly && !submissionLocked && !managerCorrectionLocked;
            const isOnLeave = leaveDateKeys.has(key);
            const halfDayPart = halfDayLeaveByDate[key]; // 'am' | 'pm' when this day is half-day leave
            const isHalfDayWithEntry = hasEntry && isOnLeave && (halfDayPart === 'am' || halfDayPart === 'pm');
            const isHalfDayLeave = !hasEntry && isOnLeave && (halfDayPart === 'am' || halfDayPart === 'pm');
            const isFullDayLeave = isOnLeave && !hasEntry && !isHalfDayLeave;
            const publicHolidayName = publicHolidayByDate[key];
            const isPublicHoliday = !!publicHolidayName;
            const hasConflict = hasEntry && isOnLeave && !isHalfDayWithEntry;
            const showPrefill = !entry && isPastOrToday && dayEditable && isWorkingDay && !isOnLeave && !isPublicHoliday;

            // Detect whether this weekday is a contracted half-day (morning-only or afternoon-only).
            // Returns 'morning', 'afternoon', or null (full day / unknown).
            const contractedHalfDayType = (() => {
              const sched = defaults?.daySchedule && typeof defaults.daySchedule === 'object' ? defaults.daySchedule : {};
              const val = sched[String(dayOfWeek)];
              if (val === 'morning') return 'morning';
              if (val === 'afternoon') return 'afternoon';
              if (val === 'off' || val === 'full') return null;
              // Fallback: infer from other entries of the same weekday this month.
              // If every other entry ends by 12:30 → morning-only; starts after 12:30 → afternoon-only.
              const sameDayEntries = days
                .filter((d) => d.getDay() === dayOfWeek && toDateKey(d) !== key)
                .map((d) => entriesByDate[toDateKey(d)])
                .filter((e) => e && e.startTime && e.endTime);
              if (sameDayEntries.length === 0) return null;
              if (sameDayEntries.every((e) => parseHHmmToHours(e.endTime) <= 12.5)) return 'morning';
              if (sameDayEntries.every((e) => parseHHmmToHours(e.startTime) >= 12.5)) return 'afternoon';
              return null;
            })();

            // Any half-day leave (AM or PM) on a contracted half-day day = the whole day is off.
            //   e.g. morning-only Wed + AM off → leave covers only shift → full day off
            //   e.g. morning-only Wed + PM off → PM already contracted off + morning = full day off
            // The system may have auto-created a bogus opposite-shift entry; we suppress it here.
            const isContractedFullDayOff = isHalfDayWithEntry && contractedHalfDayType != null;
            // Same situation, but no opposite-shift entry exists yet (typical case for new
            // half-day leaves). We render this as a single uniform leave cell rather than
            // the two-tone split, since there is no work to show on the other half.
            const isContractedHalfDayLeave = isHalfDayLeave && contractedHalfDayType != null;

            // Contracted short shift: ≤ 4.5 h, no break, not an approved-leave day
            const isShortShift = hasEntry && !isOnLeave && entry.totalHours != null && Number(entry.totalHours) <= 4.5 && getBreakHours(entry) == null;
            return (
              <div
                key={key}
                className={`wh-cell wh-day ${isToday ? 'wh-today' : ''} ${showPrefill ? 'wh-prefill' : ''} ${!hasEntry && isWeekend ? 'wh-weekend' : ''} ${!hasEntry && isNonWorkingWeekday ? 'wh-non-working' : ''} ${isFullDayLeave || isContractedFullDayOff || isContractedHalfDayLeave || (isHalfDayLeave && !isContractedHalfDayLeave) ? 'wh-on-leave-cell' : ''} ${isHalfDayWithEntry && !isContractedFullDayOff ? 'wh-half-day-with-entry' : ''} ${isPublicHoliday ? 'wh-public-holiday' : ''} ${hasConflict ? 'wh-conflict' : ''} ${isShortShift ? 'wh-short-shift' : ''}`}
              >
                <div className="wh-day-num">{day.getDate()}</div>
                {hasConflict && (
                  <div className="wh-day-conflict">
                    <span className="badge bg-warning text-dark">Leave + hours</span>
                  </div>
                )}
                {isContractedFullDayOff && (
                  <div className="wh-day-leave">
                    <span className="badge bg-info text-dark">On leave</span>
                    <span className="wh-contracted-halfday-hint">½ day</span>
                  </div>
                )}
                {isHalfDayLeave && (
                  isContractedHalfDayLeave ? (
                    <div className="wh-day-leave">
                      <span className="badge bg-info text-dark">On leave</span>
                      <span className="wh-contracted-halfday-hint">½ day</span>
                    </div>
                  ) : (
                    <div className="wh-day-leave">
                      <span className="badge bg-info text-dark">
                        Half day ({halfDayPart === 'am' ? 'AM' : 'PM'} off)
                      </span>
                    </div>
                  )
                )}
                {isFullDayLeave && (
                  <div className="wh-day-leave">
                    <span className="badge bg-info text-dark">On leave</span>
                  </div>
                )}
                {isPublicHoliday && !hasEntry && (
                  <div className="wh-day-public-holiday">
                    <span className="badge bg-secondary">{publicHolidayName}</span>
                  </div>
                )}
                {isPublicHoliday && hasEntry && !isHalfDayLeave && (
                  <div className="wh-day-public-holiday-label small text-muted">{publicHolidayName}</div>
                )}
                {hasEntry ? (
                  <div
                    className="wh-day-entry"
                    role={readOnly || submissionLocked || managerCorrectionLocked ? 'button' : undefined}
                    tabIndex={readOnly || submissionLocked || managerCorrectionLocked ? 0 : undefined}
                    onClick={readOnly || submissionLocked || managerCorrectionLocked ? () => setModalDay({ date: key, entry }) : undefined}
                    onKeyDown={readOnly || submissionLocked || managerCorrectionLocked ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalDay({ date: key, entry }); } } : undefined}
                    style={readOnly || submissionLocked || managerCorrectionLocked ? { cursor: 'pointer' } : undefined}
                  >
                    <div className="wh-day-times wh-day-times-single-line">
                      <span className="wh-day-time-line">
                        {entry.entryMode === 'time' && entry.startTime && entry.endTime
                          ? `${formatHHmmToAmPm(entry.startTime)} – ${formatHHmmToAmPm(entry.endTime)}`
                          : entry.entryMode === 'hours' || !entry.startTime
                            ? formatDecimalHours(entry.totalHours)
                            : ''}
                      </span>
                      {entry.entryMode === 'time' && (() => {
                        // Only show a break if it actually falls within working hours
                        const hasStoredBreak = !!(entry.breakStartTime && entry.breakEndTime);
                        const breakStartH = hasStoredBreak ? parseHHmmToHours(entry.breakStartTime) : null;
                        const workEndH = entry.endTime ? parseHHmmToHours(entry.endTime) : null;
                        const storedBreakValid = breakStartH != null && workEndH != null && breakStartH < workEndH;
                        if (hasStoredBreak && storedBreakValid) {
                          return (
                            <span className="wh-day-break-inline">
                              {' '}Break {formatHHmmToAmPm(entry.breakStartTime)} – {formatHHmmToAmPm(entry.breakEndTime)}
                            </span>
                          );
                        }
                        const derivedBreak = getBreakHours(entry);
                        return derivedBreak != null ? (
                          <span className="wh-day-break-inline">
                            {' '}Break {formatDecimalHours(derivedBreak)}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    {isHalfDayWithEntry && !isContractedFullDayOff && (
                      <span className="badge bg-info text-dark wh-half-day-entry-badge">
                        Half day ({halfDayPart === 'am' ? 'AM' : 'PM'} off)
                      </span>
                    )}
                    {isShortShift && (
                      <span className="wh-half-day-chip">
                        <i className="bi bi-brightness-high" aria-hidden="true" />
                        {' '}Half day
                      </span>
                    )}
                    {entry.note && (
                      <span className="wh-day-note" title={entry.note}>
                        {entry.note.length > 25 ? `${entry.note.slice(0, 25).trim()}…` : entry.note}
                      </span>
                    )}
                    {dayEditable && (
                      <button type="button" className="btn btn-sm wh-edit-btn" onClick={(e) => { e.stopPropagation(); setModalDay({ date: key, entry }); }}>
                        Edit
                      </button>
                    )}
                    {(submissionLocked || managerCorrectionLocked) && (
                      <span className="wh-view-only-badge">
                        <i className="bi bi-lock-fill" aria-hidden="true" />
                        {' '}View only
                      </span>
                    )}
                  </div>
                ) : isWeekend ? (
                  <div className="wh-day-entry">
                    <span className="wh-day-summary wh-weekend-label">Weekend</span>
                    {dayEditable && (
                      <button type="button" className="btn btn-sm btn-outline-secondary wh-add-btn" onClick={() => setModalDay({ date: key, entry: null })}>
                        Add
                      </button>
                    )}
                  </div>
                ) : isPublicHoliday ? (
                  <div className="wh-day-entry">
                    <span className="wh-day-summary wh-public-holiday-label">{publicHolidayName}</span>
                    {dayEditable && (
                      <button type="button" className="btn btn-sm btn-outline-secondary wh-add-btn" onClick={() => setModalDay({ date: key, entry: null })}>
                        Add
                      </button>
                    )}
                  </div>
                ) : isNonWorkingWeekday ? (
                  <div className="wh-day-entry">
                    <span className="wh-day-summary wh-weekend-label">Off day</span>
                    {dayEditable && (
                      <button type="button" className="btn btn-sm btn-outline-secondary wh-add-btn" onClick={() => setModalDay({ date: key, entry: null })}>
                        Add
                      </button>
                    )}
                  </div>
                ) : showPrefill ? (
                  <div className="wh-day-entry">
                    <span className="wh-day-summary">
                      {defaults?.defaultTotalHours != null ? formatDecimalHours(defaults.defaultTotalHours) : '8h'}
                    </span>
                    {dayEditable && (
                      <button type="button" className="btn btn-sm wh-edit-btn" onClick={() => setModalDay({ date: key, entry: null })}>
                        Edit
                      </button>
                    )}
                  </div>
                ) : dayEditable ? (
                  <button type="button" className="btn btn-sm btn-outline-secondary wh-add-btn" onClick={() => setModalDay({ date: key, entry: null })}>
                    Add
                  </button>
                ) : (
                  <div className="wh-day-entry"><span className="wh-day-summary text-muted">–</span></div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modalDay && (() => {
        const halfPart = halfDayLeaveByDate[modalDay.date];
        const slot = halfPart === 'am' ? HALF_DAY_WORK_SLOT.am : halfPart === 'pm' ? HALF_DAY_WORK_SLOT.pm : null;
        const halfDayDefaults = slot && !modalDay.entry
          ? (() => {
              const sH = parseHHmmToHours(slot.startTime);
              const eH = parseHHmmToHours(slot.endTime);
              const total = sH != null && eH != null ? Math.round((eH - sH) * 100) / 100 : null;
              return {
                ...(defaults || {}),
                defaultStartTime: slot.startTime,
                defaultEndTime: slot.endTime,
                defaultTotalHours: total,
                breakStartTime: '',
                breakEndTime: '',
              };
            })()
          : defaults;
        return (
          <DayEntryModal
            date={modalDay.date}
            entry={modalDay.entry}
            defaults={halfDayDefaults}
            onSave={handleSaveDay}
            onDelete={handleDeleteDay}
            onClose={() => setModalDay(null)}
            saving={saving}
            isDateInApprovedLeave={leaveDateKeys.has(modalDay.date) && !(halfDayLeaveByDate[modalDay.date] === 'am' || halfDayLeaveByDate[modalDay.date] === 'pm')}
            readOnly={readOnly || modalLockedBySubmission || modalLockedByManagerCorrection}
          />
        );
      })()}
    </div>
  );
}

export default WorkingHoursCalendar;
